<!-- apps/web/src/views/WizardEditor.vue -->
<template>
  <div class="view-wizardeditor">
    <Loading v-model="loading" />

    <PageHeader
      :title="wizardName || $t('wizards.title')"
      icon="mdi-wizard-hat"
      :breadcrumbs="breadcrumbs"
    />

    <v-container v-if="user.superUser">
      <v-alert v-if="error" type="error" dismissible>
        {{ error }}
      </v-alert>

      <v-tabs v-model="activeTab" v-if="parsedConfig">
        <v-tab key="test">{{ $t('test.editor.tabs.test') }}</v-tab>
        <v-tab key="json">{{ $t('test.editor.tabs.jsonSource') }}</v-tab>
      </v-tabs>

      <v-tabs-items v-model="activeTab" v-if="parsedConfig">
        <v-tab-item key="test">
          <TestEditor
            :test="parsedConfig"
            :has-unsaved-changes="hasUnsavedChanges"
            :can-save-draft="canSaveDraft"
            :can-publish="canPublish"
            :is-draft="isDraft"
            :has-changes-from-published="hasChangesFromPublished"
            :last-publish-time="lastPublishTime"
            @input="onEditorChange"
            @published="onPublished"
            @draft-saved="onDraftSaved"
          />
        </v-tab-item>
        <v-tab-item key="json">
          <TestJsonEditor
            :value="parsedConfig"
            @input="onJsonEditorChange"
          />
        </v-tab-item>
      </v-tabs-items>
    </v-container>

    <v-container v-else>
      <v-alert type="warning">
        {{ $t('test.editor.noPermissions') }}
      </v-alert>
    </v-container>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import Loading from '@/components/Loading.vue';
import PageHeader from '@/components/PageHeader.vue';
import TestEditor from '@/components/TestEditor.vue';
import TestJsonEditor from '@/components/TestJsonEditor.vue';

export default {
  name: 'WizardEditor',
  components: {
    Loading,
    PageHeader,
    TestEditor,
    TestJsonEditor,
  },

  data: () => ({
    activeTab: 0,
    loading: false,
    originalConfig: null,
    lastPublishTime: null,
  }),

  computed: {
    ...mapGetters('wizards', ['config', 'isDraft', 'publishedConfig', 'currentWizardId']),

    user() {
      return this.$store.getters.user || {};
    },

    storeLoading() {
      return this.$store.getters['wizards/loading'];
    },

    error() {
      return this.$store.getters['wizards/error'];
    },

    parsedConfig() {
      return this.config || null;
    },

    wizardName() {
      const wizard = this.$store.getters['wizards/docs'].find(
        (w) => w.id === this.$route.params.id,
      );
      return wizard ? wizard.name : '';
    },

    breadcrumbs() {
      return [
        { text: this.$t('wizards.title'), to: { name: 'Wizards' } },
        { text: this.wizardName || '...' },
      ];
    },

    hasUnsavedChanges() {
      return JSON.stringify(this.originalConfig) !== JSON.stringify(this.config);
    },

    hasChangesFromPublished() {
      return JSON.stringify(this.publishedConfig) !== JSON.stringify(this.config);
    },

    canSaveDraft() {
      return this.hasUnsavedChanges;
    },

    canPublish() {
      return this.hasChangesFromPublished;
    },
  },

  watch: {
    config: {
      handler(newConfig) {
        if (newConfig && this.originalConfig === null) {
          this.originalConfig = JSON.parse(JSON.stringify(newConfig));
        }
      },
      deep: true,
    },
    storeLoading(newValue) {
      this.loading = newValue;
    },
  },

  async created() {
    if (this.user.superUser) {
      await this.initWizardEditor(this.$route.params.id);
    }
  },

  beforeDestroy() {
    this.destroyWizardEditor();
  },

  beforeRouteLeave(to, from, next) {
    if (this.hasUnsavedChanges) {
      // eslint-disable-next-line no-alert
      const leave = window.confirm(this.$t('common.unsavedChanges') || 'Hai modifiche non salvate. Vuoi uscire?');
      if (leave) {
        next();
      } else {
        next(false);
      }
    } else {
      next();
    }
  },

  methods: {
    ...mapActions('wizards', {
      initWizardEditor: 'INIT_WIZARD_EDITOR',
      destroyWizardEditor: 'DESTROY_WIZARD_EDITOR',
      updateConfig: 'updateConfig',
    }),

    onEditorChange(newConfig) {
      this.updateConfig(newConfig);
    },

    onJsonEditorChange(newConfig) {
      this.updateConfig(newConfig);
    },

    onPublished() {
      this.originalConfig = JSON.parse(JSON.stringify(this.config));
    },

    onDraftSaved() {
      this.originalConfig = JSON.parse(JSON.stringify(this.config));
    },
  },
};
</script>
